import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Crown, Calendar, Users, Trophy } from "lucide-react";
import { sfx } from "@/lib/sfx";
import { useT } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Champions() {
  const { t } = useT();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refs, setRefs] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/champion/history?weeks=8`)
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
    axios
      .get(`${API}/referral/leaderboard/monthly`)
      .then((r) => setRefs(r.data || []))
      .catch(() => setRefs([]));
  }, []);

  return (
    <div className="min-h-screen w-full grid-bg py-10 px-6 sm:px-10" data-testid="champions-page">
      <button
        data-testid="champions-back"
        onClick={() => { sfx.click(); nav(-1); }}
        className="flex items-center gap-2 text-xs tracking-[0.3em] text-white/60 hover:text-cyan-300 transition-colors"
      >
        <ChevronLeft size={14} /> {t("leaderboard.back") || "BACK"}
      </button>

      <div className="max-w-4xl mx-auto mt-6">
        <div className="text-[10px] tracking-[0.4em] text-white/40 flex items-center gap-2">
          <span className="w-8 border-t border-white/30" /> HALL OF CHAMPIONS
        </div>
        <h1 className="font-display text-5xl sm:text-6xl mt-2 tracking-tighter">
          WEEKLY <span className="text-[#FFD400]" style={{ textShadow: "0 0 24px rgba(255,212,0,0.4)" }}>CROWNS</span>
        </h1>
        <div className="text-xs tracking-[0.3em] text-white/50 mt-3">
          THE BEST DAILY RUN OF EACH PAST WEEK
        </div>

        <div className="mt-10 border border-white/10 bg-black/40 backdrop-blur-md">
          <div className="grid grid-cols-12 text-[10px] tracking-[0.3em] text-white/50 px-6 py-3 border-b border-white/10">
            <div className="col-span-2">WEEK</div>
            <div className="col-span-4">CHAMPION</div>
            <div className="col-span-3 text-right">TIME</div>
            <div className="col-span-3 text-right">SCORE</div>
          </div>
          {loading && (
            <div className="p-8 text-center text-white/50 text-xs tracking-[0.3em]" data-testid="champions-loading">
              LOADING…
            </div>
          )}
          {!loading && rows.length === 0 && (
            <div className="p-8 text-center text-white/50 text-xs tracking-[0.3em]" data-testid="champions-empty">
              NO CHAMPIONS YET · BE THE FIRST
            </div>
          )}
          {!loading &&
            rows.map((r) => (
              <div
                key={r.week_index}
                data-testid={`champion-row-${r.week_index}`}
                className="grid grid-cols-12 items-center px-6 py-3 text-sm border-b border-white/5 text-white/85"
              >
                <div className="col-span-2 flex items-center gap-2 text-[11px] tracking-[0.2em] text-white/60 tabular-nums">
                  <Calendar size={11} /> {r.week_start.slice(5)} → {r.week_end.slice(5)}
                </div>
                <div className="col-span-4 uppercase tracking-widest flex items-center gap-2 truncate">
                  {r.player_name ? (
                    <>
                      {r.player_name}
                      <Crown
                        size={12}
                        className="text-[#FFD400] shrink-0"
                        style={{ filter: "drop-shadow(0 0 6px rgba(255,212,0,0.7))" }}
                      />
                    </>
                  ) : (
                    <span className="text-white/30">— NO CHAMPION —</span>
                  )}
                </div>
                <div className="col-span-3 text-right tabular-nums text-white/70">
                  {r.player_name ? `${r.survival_time.toFixed(1)}s` : "—"}
                </div>
                <div className="col-span-3 text-right tabular-nums font-bold">
                  {r.player_name ? String(r.score).padStart(6, "0") : "—"}
                </div>
              </div>
            ))}
        </div>

        {/* Referral Leaderboard */}
        <div className="mt-12 border border-white/10 bg-black/40 backdrop-blur-md p-6" data-testid="referral-leaderboard">
          <div className="flex items-center gap-2 text-[10px] tracking-[0.4em] text-[#F0ABFC] mb-3">
            <Users size={12} /> TOP REFERRERS · THIS MONTH
          </div>
          <p className="text-xs text-white/50 tracking-wider mb-4">
            The players sending the most escapees our way. Share your link at{" "}
            <button
              onClick={() => nav("/referrals")}
              className="underline text-white/70 hover:text-[#F0ABFC]"
            >/referrals</button>{" "}to join the board.
          </p>
          {refs.length === 0 ? (
            <div className="text-[10px] tracking-[0.3em] text-white/40 text-center py-4">
              NO REFERRERS YET · BE THE FIRST
            </div>
          ) : (
            <div className="space-y-2">
              {refs.map((r) => (
                <div
                  key={r.code}
                  data-testid={`referrer-row-${r.rank}`}
                  className="grid grid-cols-12 gap-2 items-center text-xs border-b border-white/5 py-2"
                >
                  <div className="col-span-1 text-white/40 font-mono">#{r.rank}</div>
                  <div className="col-span-5 flex items-center gap-2">
                    {r.rank === 1 && <Trophy size={12} className="text-[#FFD400]" />}
                    <span className="text-white font-mono">{r.handle}</span>
                    <span className="text-[10px] tracking-[0.25em] text-white/40">{r.code}</span>
                  </div>
                  <div className="col-span-2 text-right tabular-nums text-white/70">{r.clicks}<span className="text-[10px] text-white/40"> clk</span></div>
                  <div className="col-span-2 text-right tabular-nums text-[#39FF14]">{r.conversions}<span className="text-[10px] text-white/40"> sales</span></div>
                  <div className="col-span-2 text-right tabular-nums text-[#FFD400] font-bold">${(r.earned_cents / 100).toFixed(2)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
